import { action, observable, runInAction } from "mobx";
import { DomainStore } from "./domainStore";
import { toastError, toastSuccess } from "domain/errorHandling/toaster";
import {
  MCPAPIKey,
  MCPAPIKeyWithSecret,
  MCPConnection,
  createMCPKey,
  listMCPConnections,
  listMCPKeys,
  revokeConnection,
  revokeMCPKey,
} from "./persistence/persistMCPSettings";

/**
 * State for the MCP Settings page (/settings/mcp). Consumes the Item 3
 * endpoints (/v1/mcp/keys + /v1/mcp/connections). The account is always derived
 * from the authenticated session server-side; nothing here passes accountId.
 */
export class MCPStore {
  rootStore: DomainStore;
  constructor(rootStore: DomainStore) {
    this.rootStore = rootStore;
  }

  @observable
  apiKeys: MCPAPIKey[] = [];

  @observable
  connections: MCPConnection[] = [];

  @observable
  loadingKeys = false;

  @observable
  loadingConnections = false;

  @observable
  creatingKey = false;

  @observable
  keysError?: string;

  // True when the server refused the key list (403): the person has no MCP & AI
  // access (taken away after the page loaded), which a reload does not change.
  @observable
  keysForbidden = false;

  @observable
  connectionsError?: string;

  // As keysForbidden, for the connected apps list.
  @observable
  connectionsForbidden = false;

  // The plaintext key is returned exactly once on creation. Hold it so the UI
  // can show it in a one-time dialog, then discard.
  @observable
  newlyCreatedKey?: MCPAPIKeyWithSecret;

  @action
  async loadAPIKeys() {
    runInAction(() => {
      this.loadingKeys = true;
      this.keysError = undefined;
      this.keysForbidden = false;
    });
    try {
      const keys = await listMCPKeys();
      runInAction(() => {
        this.apiKeys = keys;
      });
    } catch (e) {
      const forbidden = (e as { status?: number }).status === 403;
      runInAction(() => {
        this.keysError = (e as Error).message;
        this.keysForbidden = forbidden;
      });
      // No toast: the page's bar says the list did not load (and offers Try
      // again), or, for a 403, that the access is gone.
    } finally {
      runInAction(() => {
        this.loadingKeys = false;
      });
    }
  }

  @action
  async createAPIKey(name: string, scopes: string[]) {
    runInAction(() => {
      this.creatingKey = true;
    });
    try {
      const created = await createMCPKey(name, scopes);
      runInAction(() => {
        this.newlyCreatedKey = created;
      });
      // Refresh the list so the new (secret-stripped) row appears.
      await this.loadAPIKeys();
      return created;
    } finally {
      // A failed create raises no toast: the form shows the server's reason
      // where the person is looking, and the error reaches it by the throw.
      runInAction(() => {
        this.creatingKey = false;
      });
    }
  }

  @action
  clearNewlyCreatedKey() {
    runInAction(() => {
      this.newlyCreatedKey = undefined;
    });
  }

  @action
  async revokeAPIKey(id: string) {
    try {
      await revokeMCPKey(id);
      toastSuccess("✅ MCP key revoked.");
      await this.loadAPIKeys();
    } catch (e) {
      toastError({
        message: "Error while revoking MCP key.",
        extra: e,
      });
      throw e;
    }
  }

  @action
  async loadConnectedClients() {
    runInAction(() => {
      this.loadingConnections = true;
      this.connectionsError = undefined;
      this.connectionsForbidden = false;
    });
    try {
      const connections = await listMCPConnections();
      runInAction(() => {
        this.connections = connections;
      });
    } catch (e) {
      const forbidden = (e as { status?: number }).status === 403;
      runInAction(() => {
        this.connectionsError = (e as Error).message;
        this.connectionsForbidden = forbidden;
      });
      // No toast, as for the keys: the page's bar says it.
    } finally {
      runInAction(() => {
        this.loadingConnections = false;
      });
    }
  }

  /** Disconnect one person's sign-in of an app (one row of the list). */
  @action
  async revokeClient(clientId: string, teamMemberId?: number | null) {
    try {
      await revokeConnection(clientId, teamMemberId);
      toastSuccess("✅ App disconnected.");
      await this.loadConnectedClients();
    } catch (e) {
      toastError({
        message: "Error while disconnecting app.",
        extra: e,
      });
      throw e;
    }
  }
}
